import React, { useState, useCallback } from 'react';
import { Wallet, Check, AlertCircle, Loader2, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const WalletConnect = ({ onConnect, onDisconnect, className = '' }) => {
  const { toast } = useToast();
  const [isConnecting, setIsConnecting] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [walletAddress, setWalletAddress] = useState(null);
  const [challengeData, setChallengeData] = useState(null);
  const [step, setStep] = useState('idle'); // idle, requesting, signing, verifying

  // Check if Web3 is available (MetaMask, Coinbase Wallet, etc.)
  const hasWeb3 = typeof window !== 'undefined' && window.ethereum;

  const requestWalletConnection = useCallback(async () => {
    if (!hasWeb3) {
      toast({
        title: 'Wallet Not Found',
        description: 'Please install Coinbase Wallet or MetaMask to continue.',
        variant: 'destructive'
      });
      return;
    }

    setIsConnecting(true);
    setStep('requesting');

    try {
      // Request account access
      const accounts = await window.ethereum.request({
        method: 'eth_requestAccounts'
      });

      if (!accounts || accounts.length === 0) {
        throw new Error('No accounts found');
      }

      const address = accounts[0];
      setWalletAddress(address);

      // Request challenge from backend
      const challengeResponse = await axios.post(`${BACKEND_URL}/api/wallet/connect`, {
        address
      });

      setChallengeData(challengeResponse.data);
      setStep('signing');

      // Request signature from wallet
      const signature = await window.ethereum.request({
        method: 'personal_sign',
        params: [challengeResponse.data.challenge_message, address]
      });

      setStep('verifying');

      // Verify signature with backend
      const verifyResponse = await axios.post(`${BACKEND_URL}/api/wallet/verify`, {
        address,
        signature,
        challenge_id: challengeResponse.data.challenge_id
      });

      if (verifyResponse.data.success) {
        setIsConnected(true);
        setStep('idle');
        
        // Store wallet info in localStorage
        localStorage.setItem('walletAddress', address);
        localStorage.setItem('walletConnected', 'true');
        
        toast({
          title: 'Wallet Connected',
          description: `Connected to ${address.slice(0, 6)}...${address.slice(-4)}`,
        });

        if (onConnect) {
          onConnect(address);
        }
      }
    } catch (error) {
      console.error('Wallet connection error:', error);
      setStep('idle');
      
      let errorMessage = 'Failed to connect wallet. Please try again.';
      if (error.code === 4001) {
        errorMessage = 'Connection request was rejected.';
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }
      
      toast({
        title: 'Connection Failed',
        description: errorMessage,
        variant: 'destructive'
      });
    } finally {
      setIsConnecting(false);
    }
  }, [hasWeb3, onConnect, toast]);

  const disconnectWallet = useCallback(() => {
    setIsConnected(false);
    setWalletAddress(null);
    setChallengeData(null);
    localStorage.removeItem('walletAddress');
    localStorage.removeItem('walletConnected');
    
    toast({
      title: 'Wallet Disconnected',
      description: 'Your wallet has been disconnected.',
    });

    if (onDisconnect) {
      onDisconnect();
    }
  }, [onDisconnect, toast]);

  // Check for existing connection on mount
  React.useEffect(() => {
    const storedAddress = localStorage.getItem('walletAddress');
    const storedConnected = localStorage.getItem('walletConnected');
    
    if (storedAddress && storedConnected === 'true') {
      setWalletAddress(storedAddress);
      setIsConnected(true);
    }
  }, []);

  // Render for connected state
  if (isConnected && walletAddress) {
    return (
      <div className={`bg-green-900/20 border border-green-500/50 rounded-lg p-4 ${className}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="bg-green-500/20 rounded-full p-2 mr-3">
              <Wallet className="h-5 w-5 text-green-400" />
            </div>
            <div>
              <div className="flex items-center text-green-400 font-semibold">
                <Check className="h-4 w-4 mr-1" />
                Wallet Connected
              </div>
              <div className="text-sm text-gray-300 font-mono">
                {walletAddress.slice(0, 10)}...{walletAddress.slice(-8)}
              </div>
            </div>
          </div>
          <Button
            onClick={disconnectWallet}
            variant="outline"
            size="sm"
            className="border-green-500/50 text-green-400 hover:bg-green-500/20"
          >
            Disconnect
          </Button>
        </div>
      </div>
    );
  }

  // Render for disconnected/connecting state
  return (
    <div className={`bg-slate-800/50 border border-blue-500/30 rounded-lg p-6 ${className}`}>
      <div className="text-center">
        <div className="bg-blue-500/20 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
          <Wallet className="h-8 w-8 text-blue-400" />
        </div>
        
        <h3 className="text-lg font-semibold text-white mb-2">Connect Your Wallet</h3>
        <p className="text-gray-400 text-sm mb-4">
          Connect your Coinbase Wallet or MetaMask to deposit crypto collateral
        </p>

        {!hasWeb3 && (
          <div className="bg-yellow-500/20 border border-yellow-500/50 rounded-lg p-3 mb-4">
            <div className="flex items-center text-yellow-400 text-sm">
              <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
              <span>No wallet detected. Install Coinbase Wallet or MetaMask.</span>
            </div>
            <div className="flex gap-2 mt-2">
              <a
                href="https://www.coinbase.com/wallet"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-400 hover:text-blue-300 flex items-center"
              >
                Coinbase Wallet <ExternalLink className="h-3 w-3 ml-1" />
              </a>
              <a
                href="https://metamask.io/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-400 hover:text-blue-300 flex items-center"
              >
                MetaMask <ExternalLink className="h-3 w-3 ml-1" />
              </a>
            </div>
          </div>
        )}

        <Button
          onClick={requestWalletConnection}
          disabled={isConnecting || !hasWeb3}
          className="w-full bg-blue-500 hover:bg-blue-600 text-white"
          data-testid="connect-wallet-btn"
        >
          {isConnecting ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              {step === 'requesting' && 'Connecting...'}
              {step === 'signing' && 'Sign Message in Wallet...'}
              {step === 'verifying' && 'Verifying...'}
            </>
          ) : (
            <>
              <Wallet className="h-4 w-4 mr-2" />
              Connect Wallet
            </>
          )}
        </Button>

        {step === 'signing' && (
          <p className="text-xs text-gray-400 mt-2">
            Please sign the message in your wallet to verify ownership.
            <br />
            This won't cost any gas fees.
          </p>
        )}
      </div>
    </div>
  );
};

export default WalletConnect;
